/**
 * @file DataOverwriteDialog.tsx
 * @description Warning dialog shown when SUPABASE cloud sync detects conflicts
 * between local data and cloud data (plan, blocks, settings).
 * 
 * IMPORTANT: This dialog is ONLY for Supabase cloud sync, NOT for intervals.icu.
 * intervals.icu sync is handled separately via SyncPromptModal and is limited
 * to training zones and threshold metrics only.
 * 
 * See DATA-STORAGE.md for the complete data storage architecture.
 */

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from '@/components/ui/button';
import { AlertTriangle, CloudDownload, Shield, ArrowRight, Cloud } from 'lucide-react';
import { useStore } from '@/lib/store';
import { cn } from '@/lib/utils';

const severityStyles = {
    info: 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30',
    warning: 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30',
    destructive: 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/30',
};

const severityIcons = {
    info: 'text-blue-500',
    warning: 'text-amber-500',
    destructive: 'text-red-500',
};

export function DataOverwriteDialog() {
    const pendingSync = useStore(state => state.pendingSync);
    const clearPendingSync = useStore(state => state.clearPendingSync);

    const handleAccept = () => {
        if (pendingSync.applyFn) {
            pendingSync.applyFn();
        }
        clearPendingSync();
    };

    const handleReject = () => {
        clearPendingSync();
    };

    if (!pendingSync.active || pendingSync.changes.length === 0) return null;

    const hasDestructive = pendingSync.changes.some(c => c.severity === 'destructive');

    return (
        <Dialog open={pendingSync.active} onOpenChange={(v) => { if (!v) handleReject(); }}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Cloud className={cn("w-5 h-5", hasDestructive ? "text-red-500" : "text-blue-500")} />
                        Account Data Sync
                    </DialogTitle>
                    <DialogDescription>
                        Your <span className="font-semibold">Supabase cloud account</span> has different data than your current session.
                        This is <span className="font-semibold">not</span> from intervals.icu — it's your saved account data.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-2 py-2 max-h-64 overflow-y-auto">
                    {pendingSync.changes.map((change, i) => (
                        <div
                            key={i}
                            className={cn(
                                "rounded-lg border p-3 space-y-1",
                                severityStyles[change.severity]
                            )}
                        >
                            <div className="flex items-center gap-2">
                                <AlertTriangle className={cn("w-3.5 h-3.5 shrink-0", severityIcons[change.severity])} />
                                <span className="text-sm font-medium">{change.label}</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground pl-5">
                                <span className="truncate max-w-[160px]">{change.oldSummary}</span>
                                <ArrowRight className="w-3 h-3 shrink-0" />
                                <span className="truncate max-w-[160px]">{change.newSummary}</span>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="rounded-lg border p-3 bg-muted/30">
                    <p className="text-xs text-muted-foreground">
                        {hasDestructive
                            ? "Accepting will replace your current training plan and/or blocks with the cloud version. This cannot be undone."
                            : "These changes will update some of your settings to match the cloud version."
                        }
                    </p>
                </div>

                <DialogFooter className="flex-col gap-2 sm:flex-row">
                    <Button
                        variant="outline"
                        onClick={handleReject}
                        className="w-full sm:w-auto gap-2"
                    >
                        <Shield className="w-4 h-4" />
                        Keep local data
                    </Button>
                    <Button
                        variant={hasDestructive ? "destructive" : "default"}
                        onClick={handleAccept}
                        className="w-full sm:w-auto gap-2"
                    >
                        <CloudDownload className="w-4 h-4" />
                        Accept cloud changes
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
